import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save, Phone, Mail, Globe, MapPin
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { ImageUploader } from '../ImageUpload';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}><X className="w-6 h-6" /></button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder, multiline = false }) {
  const [activeLang, setActiveLang] = useState('it');
  const Component = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (<TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.flag}</TabsTrigger>))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Component value={value?.[lang.code] || ''} onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })} placeholder={`${placeholder} (${lang.name})`} rows={multiline ? 4 : undefined} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export function AdminAccommodations() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchItems();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchItems = async () => {
    try { 
      const res = await axios.get(`${API}/accommodations?active_only=false`); 
      setItems(res.data); 
    }
    catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing.id && items.find(i => i.id === editing.id)) {
        await axios.put(`${API}/accommodations/${editing.id}`, editing);
        toast.success('Alloggio aggiornato');
      } else {
        await axios.post(`${API}/accommodations`, editing);
        toast.success('Alloggio creato');
      }
      setShowDialog(false); 
      fetchItems();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo alloggio?')) return;
    try { 
      await axios.delete(`${API}/accommodations/${id}`); 
      toast.success('Alloggio eliminato'); 
      fetchItems(); 
    }
    catch (error) { toast.error('Errore nell\'eliminazione'); }
  };

  const openNew = () => {
    setEditing({
      name: '',
      description: { it: '', en: '', fr: '', de: '', es: '' },
      contact_phone: '',
      contact_email: '',
      website: '',
      address: '',
      images: [],
      is_active: true,
      order: items.length
    });
    setShowDialog(true);
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-accommodations">
      <AdminSidebar currentPath="/admin/accommodations" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Alloggi</h1>
          <Button onClick={openNew} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Nuovo Alloggio</Button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <Bed className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Nessun alloggio registrato</p>
              <Button onClick={openNew} className="btn-primary">Aggiungi il primo alloggio</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-lg border overflow-hidden">
                  <div className="aspect-video bg-gray-100">
                    {item.images?.[0] ? (
                      <img 
                        src={item.images[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.images[0]}` : item.images[0]} 
                        alt={item.name} 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Bed className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-heading text-lg">{item.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {item.is_active ? 'Attivo' : 'Nascosto'}
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm mb-3 line-clamp-2">{item.description?.it || ''}</p>
                    
                    <div className="space-y-1 mb-4 text-sm">
                      {item.address && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate">{item.address}</span>
                        </div>
                      )}
                      {item.contact_phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{item.contact_phone}</span>
                        </div>
                      )}
                      {item.contact_email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{item.contact_email}</span>
                        </div>
                      )}
                      {item.website && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Globe className="w-4 h-4" />
                          <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-[#FF6D00] hover:underline truncate">
                            Visita sito
                          </a>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditing(item); setShowDialog(true); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? 'Modifica Alloggio' : 'Nuovo Alloggio'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nome struttura</label>
                <Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} placeholder="es. B&B La Roccia" />
              </div>
              
              <TranslatedInput 
                label="Descrizione" 
                value={editing.description} 
                onChange={(v) => setEditing({...editing, description: v})} 
                placeholder="Descrizione della struttura" 
                multiline 
              />
              
              <div>
                <label className="block text-sm font-medium mb-2">Indirizzo</label>
                <Input value={editing.address} onChange={(e) => setEditing({...editing, address: e.target.value})} placeholder="Via Roma 1, Caltavuturo" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Telefono</label>
                  <Input value={editing.contact_phone} onChange={(e) => setEditing({...editing, contact_phone: e.target.value})} placeholder="+39 123 456 7890" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input type="email" value={editing.contact_email} onChange={(e) => setEditing({...editing, contact_email: e.target.value})} placeholder="info@example.com" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Sito Web</label>
                <Input value={editing.website} onChange={(e) => setEditing({...editing, website: e.target.value})} placeholder="https://www.example.com" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Immagini</label>
                <ImageUploader 
                  value={editing.images} 
                  onChange={(images) => setEditing({...editing, images: images.map(i => typeof i === 'string' ? i : i.url)})}
                  category="accommodations"
                  maxImages={5}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Ordine visualizzazione</label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({...editing, order: parseInt(e.target.value) || 0})} />
                </div>
                <div className="flex items-center gap-2 mt-8">
                  <input 
                    type="checkbox" 
                    id="is_active"
                    checked={editing.is_active} 
                    onChange={(e) => setEditing({...editing, is_active: e.target.checked})} 
                  />
                  <label htmlFor="is_active" className="text-sm">Visibile sul sito</label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
